// Vendor-side photo upload. Parallels lib/jobPhotos.ts but:
//   * Sets field_job_photos.uploaded_by_vendor_user_id (NOT uploaded_by)
//   * Caller passes the vendor_user_id explicitly; RLS will verify it
//     matches the caller's session row.
//
// Reads remain via the shared lib/jobPhotos.ts (listJobPhotos +
// getJobPhotoSignedUrl) — table RLS scopes vendor sessions automatically
// through the field_job_photos.vendor_read policy.

import { supabase } from '@/lib/supabase'

const BUCKET = 'field-job-photos'
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'] as const

function extFromMime(mime: string): string | null {
  switch (mime) {
    case 'image/jpeg': return 'jpg'
    case 'image/png':  return 'png'
    case 'image/webp': return 'webp'
    case 'image/heic': return 'heic'
    case 'image/heif': return 'heif'
    default: return null
  }
}

export interface VendorUploadInput {
  workspaceId: string
  jobId: string
  vendorUserId: string
  file: File
  caption?: string
}

export type VendorUploadResult =
  | { data: { id: string; storage_path: string }; error: null }
  | { data: null; error: Error }

export async function uploadVendorJobPhoto({
  workspaceId, jobId, vendorUserId, file, caption,
}: VendorUploadInput): Promise<VendorUploadResult> {
  if (!ALLOWED_MIME.includes(file.type as typeof ALLOWED_MIME[number])) {
    return { data: null, error: new Error(`Unsupported file type: ${file.type || 'unknown'}`) }
  }

  // Path = {workspace_id}/{field_job_id}/{uuid}.{ext} — identical shape
  // to staff uploads, so storage policy chains and existing staff UI
  // ("Workspace members can read") see vendor uploads alongside staff
  // ones with zero special handling. The first two folder segments are
  // checked by the vendor storage INSERT policy against the caller's
  // accepted assignment.
  const ext = (file.name.split('.').pop() || extFromMime(file.type) || 'bin').toLowerCase()
  const objectId = crypto.randomUUID()
  const path = `${workspaceId}/${jobId}/${objectId}.${ext}`

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    contentType: file.type || undefined,
  })
  if (upErr) return { data: null, error: new Error(`Upload failed: ${upErr.message}`) }

  const { data: row, error: dbErr } = await supabase
    .from('field_job_photos')
    .insert({
      workspace_id: workspaceId,
      field_job_id: jobId,
      storage_path: path,
      caption: caption ?? null,
      uploaded_by: null,                            // staff column — left NULL for vendor uploads
      uploaded_by_vendor_user_id: vendorUserId,     // vendor attribution
      byte_size: file.size,
    })
    .select('id, storage_path')
    .single()

  if (dbErr || !row) {
    // Best-effort orphan cleanup so storage doesn't drift from metadata.
    await supabase.storage.from(BUCKET).remove([path]).catch(() => { /* swallow */ })
    return { data: null, error: new Error(dbErr?.message ?? 'Row insert failed') }
  }
  return { data: row as { id: string; storage_path: string }, error: null }
}
